import { v4 as uuidv4 } from 'uuid';

import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { IDecisionAct } from 'APIPackage/DecisionModule/interfaces/IDecisionAct';
import { getSelectOption } from 'KnmErknmPackage/MainModule/common/getSelectOption';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { convertKnmDataViolatedRequirementsToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolatedRequirementsToFormValues';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { IKnmActSingerSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActSingerSelectOption';
import { convertActRemarksToFormValues } from 'KnmErknmPackage/MainModule/common/convertActRemarksToFormValues';
import { convertInitialKnmDataEventsToFormData } from 'KnmErknmPackage/MainModule/common/convertInitialKnmDataEventsToFormData';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';

import { IDecisionActForm } from '../interfaces/IDecisionActForm';
import { convertSignersToFormValues } from '../../../KnmErknmPackage/MainModule/common/convertSignersToFormValues';
import { convertVideoFixationsToFormValues } from './convertDecisionVideoFixations';

export function convertDecisionDataActToFormValues(
  state: IRootState,
  act: IDecisionAct | undefined,
  passportEvents?: IKnmErknmFormEvent[],
): IDecisionActForm | undefined {
  if (!act) {
    return undefined;
  }
  const titleSignerSelectOption = act.titleSigner?.dictId
    ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
        state,
        act.titleSigner?.dictId,
        act.titleSigner?.dictVersionId,
      )
    : undefined;
  const resultContentPositionCancelOption = act.resultContentPositionCancel?.dictId
    ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
        state,
        act.resultContentPositionCancel?.dictId,
        act.resultContentPositionCancel?.dictVersionId,
      )
    : undefined;

  const knoInspectors = act.knoInspectors?.map(knoInspector => {
    const positionSelectOption = knoInspector.position?.dictId
      ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          knoInspector.position?.dictId,
          knoInspector.position?.dictVersionId,
        )
      : undefined;
    const formKnoInspector: IKnmErknmFormInspector = {
      ...knoInspector,
      position: positionSelectOption,
      listId: knoInspector.guid || uuidv4(),
      label: `${knoInspector.fullName} - ${positionSelectOption?.label}`,
    };

    return formKnoInspector;
  });

  const knoInspectorsOptions: IKnmErknmFormInspector[] | undefined = knoInspectors?.map(i => ({
    listId: i.guid || uuidv4(),
    guid: i.guid || undefined,
    knoInspector: {
      id: i?.guid || '',
      label: `${i.fullName} - ${i.position?.label}`,
      value: i,
    },
    fullName: undefined,
    position: undefined,
    dateCreate: i.dateCreate || undefined,
  }));

  let fioAndPositionSinger: IKnmActSingerSelectOption | undefined;

  if (act.fioSigner && titleSignerSelectOption) {
    fioAndPositionSinger = {
      id: '',
      label: `${act.fioSigner} - ${titleSignerSelectOption?.label}`,
      value: {
        dateCreate: null,
        fullName: act.fioSigner,
        guid: undefined,
        listId: '',
        position: titleSignerSelectOption,
        label: '',
      },
    };

    if (knoInspectors) {
      const singer = knoInspectors?.find(
        i => i.fullName === act.fioSigner && i.position?.value.dictId === act.titleSigner?.dictId,
      );

      if (singer) {
        fioAndPositionSinger = {
          id: singer?.guid || '',
          label: `${singer.fullName} - ${singer.position?.label}`,
          value: singer,
        };
      }
    }
  }

  return {
    isNew: act.isNew || undefined,
    guid: act.guid || undefined,
    nextWordDayActDateTime: act.nextWordDayActDateTime || undefined,
    created: act.created || undefined,
    creationActDateTime: act.paperless
      ? act.creationActDateTime || undefined
      : convertInitialKnmDateToFormDate(act.creationActDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS),
    signActDateTime: act.signActDateTime || undefined,
    numberAct: act.numberAct || undefined,
    document: act.document
      ? {
          active: act.document.active || undefined,
          attachments: act.document.attachments || undefined,
          correlationToken: act.document.correlationToken || undefined,
          description: act.document.description || undefined,
          guid: act.document.guid || undefined,
          type: act.document.type || undefined,
        }
      : undefined,
    signed: act.signed ?? undefined,
    paperless: act.paperless || undefined,
    extractVersion: act.extractVersion || undefined,
    isViolatedMandatoryRequirements: booleanSelectOptions.find(item => item.value === act.isViolations),
    violatedMandatoryRequirements: act.violations
      ? convertKnmDataViolatedRequirementsToFormValues(act.violations, passportEvents, act.videoFixations)
      : undefined,
    isViolationEvidenceExists: booleanSelectOptions.find(item => item.value === act.videoFixationsCheckbox),
    violationEvidences: convertVideoFixationsToFormValues(act?.videoFixations, passportEvents),
    familiarizationNotifications:
      act.familiarizationNotifications?.map(x => {
        const notification: IKnmActFamiliarizationNotificationsForm = {
          noticeDate: convertInitialKnmDateToFormDate(x.noticeDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          knmNoticeType: x.knmNoticeType ? createKnmNotificationMethodSelectOption(x.knmNoticeType) : undefined,
          note: x.note,
          listId: x.guid || uuidv4(),
          isNew: !!x.isNew,
          existedAtSigning: x.existedAtSigning || undefined,
        };

        return notification;
      }) || undefined,
    remarks: convertActRemarksToFormValues(act.remarks),
    events: act.events ? convertInitialKnmDataEventsToFormData(act.events, state) : undefined,
    fioSigner: act.fioSigner || undefined,
    titleSigner: titleSignerSelectOption,
    fioAndPositionSinger,
    holdingDateTime: convertInitialKnmDateToFormDate(
      act.holdingDateTime,
      DATE_FORMAT_LONG_MINUTE_FNS,
      DATE_FORMAT_ISO_FNS,
    ),
    durationDays: typeof act.durationDays === 'number' ? act.durationDays.toString() : undefined,
    durationHours: typeof act.durationHours === 'number' ? act.durationHours.toString() : undefined,
    knoInspectors: knoInspectorsOptions,
    involvedWitnesses: !act.involvedWitnesses
      ? undefined
      : act.involvedWitnesses.map(i => ({
          fullName: i.fullName || undefined,
          listId: i.guid || '',
          isNew: false,
        })),
    noteInvalidate: act.noteInvalidate || undefined,
    fileInvalidate: act.fileInvalidate || undefined,
    officialFullNameCancel: act.officialFullNameCancel || undefined,
    resultContentPositionCancel: resultContentPositionCancelOption,
    firstExtractDateTime:
      convertInitialKnmDateToFormDate(act.firstExtractDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) ||
      null,
    systemSignatureDateTime: act.systemSignatureDateTime,
    systemSignatureMnemonic: act.systemSignatureMnemonic,
    systemSignatureTitle: act.systemSignatureTitle,
    systemSignatureUsed: act.systemSignatureUsed,
    signers: convertSignersToFormValues(act.signers, state, act.knoInspectors || []),
  };
}
